import { useReadContract } from "wagmi";
import { useMemo } from "react";
import { BigNumber } from "ethers";

// Import all available ABIs from the project
import ERC721_ABI from "../abi/IERC721.json";
import ERC1155_ABI from "../abi/IERC1155.json";
import ERC20_ABI from "../abi/ERC20.json";
import MARKETPLACE_ABI from "../abi/Marketplace.json";
import MARKETPLACE_FEATURE_ABI from "../abi/MarketplaceFeature.json";
import AUCTION_ABI from "../abi/Auction.json";
import COLLECTION_FACTORY_ABI from "../abi/CollectionFactory.json";
import ERC1155_LAUNCHPAD_ABI from "../abi/erc1155LaunchPadNewCollection.json";
import ERC721_LAUNCHPAD_ABI from "../abi/erc721LaunchPadNewCollection.json";
import EVENT_ABI from "../abi/event.json";
import MBC_MOCK_TOKEN_ABI from "../abi/mbcMockToken.json";
import MONBASE_LOTTERY_ABI from "../abi/MonbaseLottery.json";
import OMARKET_COLLECTION_ABI from "../abi/OmarketCollection.json";
import OP_SWAP_ABI from "../abi/OP_SWAP.json";
import OPV_REF_ABI from "../abi/OPV_REF.json";
import OPV_ABI from "../abi/opv.json";
import OPV_AUCTION_ABI from "../abi/OPVAuction.json";
import OPV_AUCTION_MULTIL_NEW_ABI from "../abi/OPVAuctionMultilNew.json";
import OPV_AUCTION_MULTI_SEC_ABI from "../abi/OPVAuctionMultiSec.json";
import OPV_FACTORY_ABI from "../abi/OPVFactory.json";
import OPV_HOLD_ABI from "../abi/OPVHold.json";
import OPV_MARKET_AND_AUCTION_ABI from "../abi/OPVMarketAndAuction.json";
import OPV_MARKETPLACE_ABI from "../abi/OPVMarketplace.json";
import OPV_MULTIPLE_FACTORY_ABI from "../abi/OPVMultipleFactory.json";
import OPV_PRICE_ABI from "../abi/OPVPrice.json";
import OPV_TRANSFER_ABI from "../abi/OPVTransfer.json";
import OPV_VESTING_ABI from "../abi/OpvVesting.json";
import PANCAKE_ABI from "../abi/pancake.json";
import PANCAKE_VSEC_ABI from "../abi/pancakeVsec.json";
import SANH_MARKETPLACE_ABI from "../abi/sanhMarketplace.json";
import SANH_PURCHASE_ABI from "../abi/SanhPurchase.json";
import TOKEN_ABI from "../abi/token_abi.json";
import AIRDROP_ABI from "../abi/airdrop.json";

// Helper function to get ABI from different formats
const getABI = (abiData: any) => {
  const result = abiData.abi || abiData;
  return result;
};

// Type definition for all supported contract types
export type ContractType = 
  | "ERC721" 
  | "ERC1155" 
  | "ERC20" 
  | "MARKETPLACE" 
  | "MARKETPLACE_FEATURE"
  | "AUCTION"
  | "COLLECTION_FACTORY"
  | "ERC1155_LAUNCHPAD"
  | "ERC721_LAUNCHPAD"
  | "EVENT"
  | "MBC_MOCK_TOKEN"
  | "MONBASE_LOTTERY"
  | "OMARKET_COLLECTION"
  | "OP_SWAP"
  | "OPV_REF"
  | "OPV"
  | "OPV_AUCTION"
  | "OPV_AUCTION_MULTIL_NEW"
  | "OPV_AUCTION_MULTI_SEC"
  | "OPV_FACTORY"
  | "OPV_HOLD"
  | "OPV_MARKET_AND_AUCTION"
  | "OPV_MARKETPLACE"
  | "OPV_MULTIPLE_FACTORY"
  | "OPV_PRICE"
  | "OPV_TRANSFER"
  | "OPV_VESTING"
  | "PANCAKE"
  | "PANCAKE_VSEC"
  | "SANH_MARKETPLACE"
  | "SANH_PURCHASE"
  | "TOKEN"
  | "AIRDROP"
  | "CUSTOM";

interface UseReadContractWithABIProps {
  contractAddress: string;
  contractType?: ContractType;
  customABI?: any;
  functionName: string;
  args?: readonly unknown[];
  enabled?: boolean;
}

/**
 * Custom hook to read contract data with pre-defined ABIs
 * Supports multiple contract types and is reusable across the application
 */
export default function useReadContractWithABI({
  contractAddress,
  contractType = "CUSTOM",
  customABI,
  functionName,
  args = [],
  enabled = true,
}: UseReadContractWithABIProps) {
  // Get ABI based on contract type
  const abi = useMemo(() => {
    if (customABI) return customABI;
    
    
    switch (contractType) {
      // Standard ERC contracts
      case "ERC721":
        return getABI(ERC721_ABI);
      case "ERC1155":
        return getABI(ERC1155_ABI);
      case "ERC20":
        return getABI(ERC20_ABI);
      
      // Marketplace contracts
      case "MARKETPLACE":
        return getABI(MARKETPLACE_ABI);
      case "MARKETPLACE_FEATURE":
        return getABI(MARKETPLACE_FEATURE_ABI);
      case "SANH_MARKETPLACE":
        return SANH_MARKETPLACE_ABI;
      case "SANH_PURCHASE":
        return SANH_PURCHASE_ABI;
      
      // Auction contracts
      case "AUCTION":
        return AUCTION_ABI;
      case "OPV_AUCTION":
        return OPV_AUCTION_ABI.abi;
      case "OPV_AUCTION_MULTIL_NEW":
        return OPV_AUCTION_MULTIL_NEW_ABI.abi;
      case "OPV_AUCTION_MULTI_SEC":
        return OPV_AUCTION_MULTI_SEC_ABI;
      
      // Factory contracts
      case "COLLECTION_FACTORY":
        return COLLECTION_FACTORY_ABI;
      case "OPV_FACTORY":
        return OPV_FACTORY_ABI;
      case "OPV_MULTIPLE_FACTORY":
        return OPV_MULTIPLE_FACTORY_ABI.abi;
      
      // Launchpad contracts
      case "ERC1155_LAUNCHPAD":
        return ERC1155_LAUNCHPAD_ABI;
      case "ERC721_LAUNCHPAD":
        return ERC721_LAUNCHPAD_ABI;
      
      // OPV contracts
      case "OPV":
        return OPV_ABI.abi;
      case "OPV_REF":
        return OPV_REF_ABI.abi;
      case "OPV_HOLD":
        return OPV_HOLD_ABI.abi;
      case "OPV_MARKET_AND_AUCTION":
        return OPV_MARKET_AND_AUCTION_ABI;
      case "OPV_MARKETPLACE":
        return OPV_MARKETPLACE_ABI.abi;
      case "OPV_PRICE":
        return OPV_PRICE_ABI;
      case "OPV_TRANSFER":
        return OPV_TRANSFER_ABI.abi;
      case "OPV_VESTING":
        return OPV_VESTING_ABI;
      
      // Other contracts
      case "EVENT":
        return EVENT_ABI.abi;
      case "MBC_MOCK_TOKEN":
        return MBC_MOCK_TOKEN_ABI;
      case "MONBASE_LOTTERY":
        return MONBASE_LOTTERY_ABI.abi;
      case "OMARKET_COLLECTION":
        return OMARKET_COLLECTION_ABI;
      case "OP_SWAP":
        return OP_SWAP_ABI.abi;
      case "PANCAKE":
        return PANCAKE_ABI;
      case "PANCAKE_VSEC":
        return PANCAKE_VSEC_ABI;
      case "TOKEN":
        return TOKEN_ABI;
      case "AIRDROP":
        return AIRDROP_ABI.abi;
      
      default:
        return null;
    }
  }, [contractType, customABI]);


  const result = useReadContract({
    address: contractAddress as `0x${string}`,
    abi,
    functionName,
    args,
    query: {
      enabled: enabled && !!contractAddress && !!abi,
    },
  });


  return result;
}

/**
 * Specialized hook to check approval status for ERC721/ERC1155 contracts
 */
export function useApprovalStatus(
  contractAddress: string,
  ownerAddress: string,
  operatorAddress: string,
  contractType: "ERC721" | "ERC1155" = "ERC1155"
) {
  return useReadContractWithABI({
    contractAddress,
    contractType,
    functionName: "isApprovedForAll",
    args: [ownerAddress as `0x${string}`, operatorAddress as `0x${string}`],
    enabled: !!contractAddress && !!ownerAddress && !!operatorAddress,
  });
}

/**
 * Specialized hook to read ERC20 token balance
 * Returns BigNumber type for better type safety
 */
export function useTokenBalance(
  contractAddress: string,
  ownerAddress: string
) {
  const result = useReadContractWithABI({
    contractAddress,
    contractType: "ERC20",
    functionName: "balanceOf",
    args: [ownerAddress as `0x${string}`],
    enabled: !!contractAddress && !!ownerAddress,
  });

  return {
    ...result,
    // Cast data to BigNumber for type safety - handle BigInt properly
    data: result.data !== undefined ? BigNumber.from(result.data.toString()) : undefined,
  };
}

/**
 * Specialized hook to read ERC20 token allowance
 * Returns BigNumber type for better type safety
 */
export function useTokenAllowance(
  contractAddress: string,
  ownerAddress: string,
  spenderAddress: string
) {
  const result = useReadContractWithABI({
    contractAddress,
    contractType: "ERC20",
    functionName: "allowance",
    args: [ownerAddress as `0x${string}`, spenderAddress as `0x${string}`],
    enabled: !!contractAddress && !!ownerAddress && !!spenderAddress,
  });


  return {
    ...result,
    // Cast data to BigNumber for type safety - handle BigInt properly
    data: result.data !== undefined ? BigNumber.from(result.data.toString()) : undefined,
  };
}

/**
 * Specialized hook to read auction information
 */
export function useAuctionInfo(contractAddress: string, auctionId: string) {
  return useReadContractWithABI({
    contractAddress,
    contractType: "OPV_AUCTION",
    functionName: "getAuction",
    args: [auctionId],
    enabled: !!contractAddress && !!auctionId,
  });
}

/**
 * Specialized hook to read lottery information
 */
export function useLotteryInfo(contractAddress: string, lotteryId: string) {
  return useReadContractWithABI({
    contractAddress,
    contractType: "MONBASE_LOTTERY",
    functionName: "getLottery",
    args: [lotteryId],
    enabled: !!contractAddress && !!lotteryId,
  });
}

/**
 * Specialized hook to read price from OPV Price contract
 */
export function useOPVPrice(contractAddress: string) {
  return useReadContractWithABI({
    contractAddress,
    contractType: "OPV_PRICE",
    functionName: "getPrice",
    enabled: !!contractAddress,
  });
}

/**
 * Specialized hook to read vesting information
 */
export function useVestingInfo(contractAddress: string, userAddress: string) {
  return useReadContractWithABI({
    contractAddress,
    contractType: "OPV_VESTING",
    functionName: "getVestingInfo",
    args: [userAddress as `0x${string}`],
    enabled: !!contractAddress && !!userAddress,
  });
}

/**
 * Specialized hook to read factory information
 */
export function useFactoryInfo(contractAddress: string, collectionId: string) {
  return useReadContractWithABI({
    contractAddress,
    contractType: "OPV_FACTORY",
    functionName: "getCollection",
    args: [collectionId],
    enabled: !!contractAddress && !!collectionId,
  });
}
